import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'designSystem/components/HoverCard'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { useIsTouchMobile } from 'designSystem/hooks/useIsTouchDevice'
import {
  EVIDENCE_CHIP_HOVER_CLOSE_DELAY_MS,
  EVIDENCE_CHIP_HOVER_OPEN_DELAY_MS,
  EVIDENCE_CHIP_SIDE_OFFSET_PX,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/evidenceChipHoverOpen'
import { EvidencePreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/EvidencePreview'
import { useEvidenceChipHoverOpen } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/useEvidenceChipHoverOpen'
import type { CitationFailureReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/components/DocumentThumbnail'
import { CircleAlert, ExternalLink } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

const CHIP_LABEL_MAX_CHARS = 12

export const TriggerHitArea = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: middle;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: ${EVIDENCE_CHIP_SIDE_OFFSET_PX}px;
  }

  &::before {
    bottom: 100%;
  }

  &::after {
    top: 100%;
  }
`

const WarningIcon = styled(CircleAlert)`
  color: var(--warning);
`

function truncateChipLabel(label: string): string {
  if (label.length <= CHIP_LABEL_MAX_CHARS) return label
  return `${label.slice(0, CHIP_LABEL_MAX_CHARS).trimEnd()}…`
}

interface EvidenceChipProps {
  documentId?: string
  title?: string
  drawing?: string
  filename?: string
  bqDocumentId?: string
  cantileverDocumentEntryId?: string
  documentHostsHydrated?: boolean
  pageNum: number
  showPageNumber?: boolean
  quotes: string[]
  failureReason?: CitationFailureReason | null
  onClick?: () => void
  onOpenInNewTab?: () => void
  onOpenChange?: (open: boolean) => void
}

export function EvidenceChip(props: EvidenceChipProps): React.ReactElement {
  // Phone-sized coarse pointers only. `(pointer: coarse)` alone is true on
  // Windows touchscreen laptops, which swaps the hover chip ("Open in new
  // tab") for the tap chip ("Open page N") and makes chip clicks toggle a
  // popover instead of opening the preview panel.
  const isTouchMobile = useIsTouchMobile()
  return isTouchMobile ? (
    <TapEvidenceChip {...props} />
  ) : (
    <HoverEvidenceChip {...props} />
  )
}

function useEvidenceChipDisplay({
  title,
  drawing,
  filename,
  pageNum,
  showPageNumber = true,
  failureReason,
  documentId,
  onClick,
  onOpenInNewTab,
}: EvidenceChipProps) {
  const { t } = useLingui()
  const isFailed = failureReason !== null && failureReason !== undefined
  const chipLabel = title ?? drawing ?? filename
  const pageLabel = t`Page ${pageNum}`
  const truncatedChipLabel = chipLabel ? truncateChipLabel(chipLabel) : ''
  return {
    isFailed,
    quoteColor: isFailed ? ('amber' as const) : ('green' as const),
    canOpen: Boolean(documentId && onClick),
    canOpenInNewTab: Boolean(documentId && onOpenInNewTab && !isFailed),
    chipLabel,
    chipText: chipLabel
      ? showPageNumber
        ? t`${truncatedChipLabel} · p.${pageNum}`
        : truncatedChipLabel
      : pageLabel,
    previewTitle: chipLabel
      ? showPageNumber
        ? t`${chipLabel} · p.${pageNum}`
        : chipLabel
      : pageLabel,
    openPageLabel: showPageNumber ? t`Open page ${pageNum}` : t`Open`,
    openInNewTabLabel: t`Open in new tab`,
  }
}

function EvidenceChipPreview({
  documentId,
  filename,
  bqDocumentId,
  cantileverDocumentEntryId,
  documentHostsHydrated = false,
  pageNum,
  quotes,
  failureReason = null,
  chipLabel,
  previewTitle,
  quoteColor,
}: EvidenceChipProps &
  Pick<
    ReturnType<typeof useEvidenceChipDisplay>,
    'chipLabel' | 'previewTitle' | 'quoteColor'
  >): React.ReactElement {
  return (
    <EvidencePreview
      title={previewTitle}
      quotes={quotes}
      quotePalette={quoteColor}
      failureReason={failureReason}
      page={
        documentId ? (
          <DocumentThumbnail
            documentId={documentId}
            pageNumber={pageNum}
            alt={chipLabel ?? previewTitle}
            filename={filename}
            bqDocumentId={bqDocumentId}
            cantileverDocumentEntryId={cantileverDocumentEntryId}
            documentHostsHydrated={documentHostsHydrated}
          />
        ) : null
      }
    />
  )
}

function HoverEvidenceChip(props: EvidenceChipProps): React.ReactElement {
  const { onClick, onOpenChange, onOpenInNewTab } = props
  const display = useEvidenceChipDisplay(props)
  const {
    open,
    handleOpenChange,
    handleTriggerPointerEnter,
    handleTriggerPointerLeave,
    handleContentPointerEnter,
    handleContentPointerLeave,
  } = useEvidenceChipHoverOpen(onOpenChange)

  const handleOpenInNewTab = useCallback(() => {
    handleOpenChange(false)
    onOpenInNewTab?.()
  }, [handleOpenChange, onOpenInNewTab])

  return (
    <HoverCard
      open={open}
      openDelay={EVIDENCE_CHIP_HOVER_OPEN_DELAY_MS}
      closeDelay={EVIDENCE_CHIP_HOVER_CLOSE_DELAY_MS}
      onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        <TriggerHitArea
          onPointerEnter={handleTriggerPointerEnter}
          onPointerLeave={handleTriggerPointerLeave}>
          <Button
            type='button'
            variant='outline'
            size='xs'
            aria-disabled={!display.canOpen}
            onClick={display.canOpen ? onClick : undefined}>
            {display.isFailed ? <WarningIcon /> : null}
            {display.chipText}
          </Button>
        </TriggerHitArea>
      </HoverCardTrigger>
      <HoverCardContent
        align='start'
        side='bottom'
        sideOffset={EVIDENCE_CHIP_SIDE_OFFSET_PX}
        onPointerEnter={handleContentPointerEnter}
        onPointerLeave={handleContentPointerLeave}>
        <Box display='flex' direction='column' gap='sm'>
          <EvidenceChipPreview {...props} {...display} />
          {display.canOpenInNewTab ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              width='full'
              onClick={handleOpenInNewTab}>
              {display.openInNewTabLabel}
              <ExternalLink data-icon='inline-end' />
            </Button>
          ) : null}
        </Box>
      </HoverCardContent>
    </HoverCard>
  )
}

function TapEvidenceChip(props: EvidenceChipProps): React.ReactElement {
  const { onClick, onOpenChange, onOpenInNewTab } = props
  const display = useEvidenceChipDisplay(props)
  const [open, setOpen] = useState(false)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [onOpenChange]
  )

  const handleOpenDocument = useCallback(() => {
    // Open the preview before dismissing the popover so keep-open-on-outside-
    // click is registered before this same tap can close the side panel.
    onClick?.()
    handleOpenChange(false)
  }, [handleOpenChange, onClick])

  const handleOpenInNewTab = useCallback(() => {
    handleOpenChange(false)
    onOpenInNewTab?.()
  }, [handleOpenChange, onOpenInNewTab])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type='button' variant='outline' size='xs' aria-expanded={open}>
          {display.isFailed ? <WarningIcon /> : null}
          {display.chipText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={EVIDENCE_CHIP_SIDE_OFFSET_PX}>
        <Box display='flex' direction='column' gap='sm'>
          <EvidenceChipPreview {...props} {...display} />
          {display.canOpen ? (
            <Button
              type='button'
              variant='outline'
              size='lg'
              width='full'
              onClick={handleOpenDocument}>
              {display.openPageLabel}
              <ExternalLink data-icon='inline-end' />
            </Button>
          ) : null}
          {display.canOpenInNewTab ? (
            <Button
              type='button'
              variant='outline'
              size='lg'
              width='full'
              onClick={handleOpenInNewTab}>
              {display.openInNewTabLabel}
              <ExternalLink data-icon='inline-end' />
            </Button>
          ) : null}
        </Box>
      </PopoverContent>
    </Popover>
  )
}
